import { mergeUiMessageStream } from "@/apps/contract-review/components/contract-type-assistant/consume-ui-stream";
import { isWriteToolName as isContractTypeWriteTool } from "@/apps/contract-review/components/contract-type-assistant/types";
import { normalizeProposalInput as normalizeContractTypeProposal } from "@/apps/contract-review/components/contract-type-assistant/types";
import { isWriteToolName as isCustomRoleWriteTool } from "@/apps/contract-review/components/custom-role-assistant/types";
import { normalizeProposalInput as normalizeCustomRoleProposal } from "@/apps/contract-review/components/custom-role-assistant/types";
import { AssistantChatErrorNotice } from "@/components/assistant/AssistantChatErrorNotice";
import { AssistantPanelPlanSplit } from "@/components/assistant/AssistantPanelPlanSplit";
import { AssistantPlanProvider, useAssistantPlan } from "@/components/assistant/AssistantPlanContext";
import { createWriteToolCallApprovalStreamHandler } from "@/components/assistant/applyWriteToolCallApprovalFromStreamPart";
import { applyWriteApprovalSuccessMessageIfNeeded } from "@/components/assistant/applyWriteApprovalSuccessMessage";
import { dedupeUiMessagesForApiRequest } from "@/components/assistant/dedupeUiMessagesForApiRequest";
import { findActiveWriteApproval } from "@/components/assistant/findActiveWriteApproval";
import { finalizeWriteApprovalStreamMessages } from "@/components/assistant/finalizeWriteApprovalStreamMessages";
import { narrowUiMessagesForMastraThreadChat } from "@/components/assistant/narrowUiMessagesForMastraThreadChat";
import { parseWriteAssistantApproveResponse } from "@/components/assistant/parseWriteAssistantApproveResponse";
import { submitAssistantChatMessage } from "@/components/assistant/submitAssistantChatMessage";
import { useAppendUserChatMessage } from "@/components/assistant/useAppendUserChatMessage";
import { useAssistantWriteApprovalHandlers } from "@/components/assistant/useAssistantWriteApprovalHandlers";
import { useAutoContinueTextOnlyWriteProposal } from "@/components/assistant/useAutoContinueTextOnlyWriteProposal";
import { useMergePendingWriteApprovals } from "@/components/assistant/useMergePendingWriteApprovals";
import { useResolvedWriteApprovals } from "@/components/assistant/useResolvedWriteApprovals";
import { useSyncAssistantPlan } from "@/components/assistant/useSyncAssistantPlan";
import { applySseProgressEvent } from "@/lib/contract-review-sse";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api-config";
import { getJwtToken } from "@/lib/auth-client";
import type { ProgressState, SSEEventData } from "@/types";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { darkPageCanvasWashClassName } from "@/lib/page-canvas";
import { cn } from "@/lib/utils";
import { refreshSupervisorChats, SUPERVISOR_NEW_CHAT } from "../supervisor-chat-nav";
import { Component, useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CONTRACT_TYPE_SPECIALIST,
  CUSTOM_ROLE_SPECIALIST,
  isSupervisorWriteToolName,
  normalizeSupervisorProposal,
  specialistForToolName,
  SUPERVISOR_THREAD_SCOPE,
} from "../specialists";
import { SupervisorApprovalProvider } from "../components/SupervisorApprovalContext";
import {
  SupervisorThread,
  type PendingAttachPreview,
  type SupervisorAttachment,
} from "../components/SupervisorThread";
import {
  toKnowledgeSearchScopePayload,
  type KnowledgeScopeSelection,
  type KnowledgeSearchScopePayload,
} from "../lib/knowledge-scope";
import { isContractReviewAttachmentIntent } from "../lib/contract-review-attachment-intent";
import { buildUserTextWithAttachmentMarker } from "../lib/chat-attachment-marker";
import { uploadGeneralChatAttachment } from "../lib/general-chat-upload";
import { ReviewQuestionProvider, type ReviewQuestionRun } from "../components/ReviewQuestionContext";
import { ReviewResultsCanvas, type ReviewCanvasDocument } from "../components/ReviewResultsCanvas";
import {
  CANVAS_CLOSE_MS,
  CanvasSplit,
  CanvasSplitDocument,
  CanvasSplitThread,
  nextCanvasSize,
  type CanvasSize,
} from "../components/elements/canvas-split";
import {
  canvasDocumentFromReview,
  latestReviewCanvasTarget,
  mergeReviewConfig,
  reviewConfigFromApi,
  type ReviewApiRow,
} from "../lib/latest-review-canvas";
import type { QuestionFlowStep } from "../components/elements/question-flow";

function applyReviewProgressPart(
  part: { type?: string; data?: unknown },
  setReviewProgress: Dispatch<SetStateAction<ProgressState | null>>
) {
  if (part.type !== "data-review-progress") return;
  const data = part.data as (SSEEventData & { heartbeat?: boolean }) | undefined;
  if (data?.heartbeat) return;
  if (typeof data?.message === "string" && data.message.trim().toLowerCase().startsWith("still working on")) {
    return;
  }
  setReviewProgress((prev) =>
    applySseProgressEvent(prev ?? { message: "", progress: 0, isConnected: true, logs: [] }, data ?? {})
  );
}

function isReviewQuestionTool(name: string): boolean {
  return name === "collect-review-inputs" || name === "collectReviewInputs";
}

function stepsFromPart(record: Record<string, unknown>): QuestionFlowStep[] | undefined {
  const data =
    record.data && typeof record.data === "object" ? (record.data as Record<string, unknown>) : undefined;
  const payload =
    data?.suspendPayload && typeof data.suspendPayload === "object"
      ? (data.suspendPayload as { steps?: QuestionFlowStep[]; args?: { steps?: QuestionFlowStep[] } })
      : undefined;
  const input =
    record.input && typeof record.input === "object"
      ? (record.input as { steps?: QuestionFlowStep[] })
      : undefined;
  const args =
    record.args && typeof record.args === "object"
      ? (record.args as { steps?: QuestionFlowStep[] })
      : undefined;
  const steps = payload?.steps ?? payload?.args?.steps ?? input?.steps ?? args?.steps;
  return Array.isArray(steps) ? steps : undefined;
}

function questionRunsFromMessages(messages: UIMessage[]): Record<string, ReviewQuestionRun> {
  const runs: Record<string, ReviewQuestionRun> = {};
  for (const message of messages) {
    if (!Array.isArray(message.parts)) continue;
    for (const part of message.parts) {
      const record = part as unknown as Record<string, unknown>;
      const type = typeof record.type === "string" ? record.type : "";
      const data =
        record.data && typeof record.data === "object" ? (record.data as Record<string, unknown>) : undefined;
      const toolName =
        (typeof record.toolName === "string" ? record.toolName : "") ||
        (typeof data?.toolName === "string" ? data.toolName : "") ||
        (type.startsWith("tool-") ? type.slice("tool-".length) : "");
      if (!isReviewQuestionTool(toolName) && type !== "data-tool-call-suspended") continue;
      if (
        type.startsWith("data-") &&
        typeof data?.toolName === "string" &&
        !isReviewQuestionTool(data.toolName)
      ) {
        continue;
      }
      const toolCallId =
        (typeof record.toolCallId === "string" ? record.toolCallId : "") ||
        (typeof data?.toolCallId === "string" ? data.toolCallId : "");
      const metadata =
        record.providerMetadata && typeof record.providerMetadata === "object"
          ? (record.providerMetadata as Record<string, unknown>)
          : undefined;
      const runId =
        (typeof data?.runId === "string" ? data.runId : "") ||
        (typeof record.runId === "string" ? record.runId : "") ||
        (typeof metadata?.runId === "string" ? metadata.runId : "");
      if (!toolCallId || !runId) continue;
      const steps = stepsFromPart(record);
      runs[toolCallId] = {
        runId,
        toolCallId,
        steps: steps ?? runs[toolCallId]?.steps,
      };
    }
  }
  return runs;
}

function questionRunsFromSuspended(suspended: {
  runId?: string;
  toolCallId?: string;
  steps?: QuestionFlowStep[];
} | null | undefined): Record<string, ReviewQuestionRun> {
  if (!suspended?.runId || !suspended.toolCallId) return {};
  return {
    [suspended.toolCallId]: {
      runId: suspended.runId,
      toolCallId: suspended.toolCallId,
      steps: Array.isArray(suspended.steps) ? suspended.steps : undefined,
    },
  };
}

function answerErrorMessage(text: string): string {
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (typeof parsed.error === "string" && parsed.error.trim()) return parsed.error;
  } catch {
    return text.trim() || "Could not save those answers.";
  }
  return text.trim() || "Could not save those answers.";
}

function messageTextContent(message: UIMessage): string {
  if (!Array.isArray(message.parts)) return "";
  return message.parts
    .filter((part): part is Extract<UIMessage["parts"][number], { type: "text" }> => part.type === "text")
    .map((part) => part.text)
    .join("\n\n")
    .trim();
}

function SupervisorChatSession() {
  const [approvalByToolCallId, setApprovalByToolCallId] = useState<
    Record<string, { runId: string; toolCallId: string; toolName: string; args: Record<string, unknown> }>
  >({});
  const [actionLoading, setActionLoading] = useState(false);
  const [routeLabel, setRouteLabel] = useState<string | null>(null);
  const [questionRuns, setQuestionRuns] = useState<Record<string, ReviewQuestionRun>>({});
  const [loadedReview, setLoadedReview] = useState<ReviewCanvasDocument | null>(null);
  const [answering, setAnswering] = useState(false);
  const [reviewProgress, setReviewProgress] = useState<ProgressState | null>(null);
  const [dismissedReviewId, setDismissedReviewId] = useState<string | null>(null);
  const [canvasSize, setCanvasSize] = useState<CanvasSize>("docked");
  const [canvasClosing, setCanvasClosing] = useState(false);
  const canvasCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchMode, setSearchMode] = useState<"off" | "web" | "knowledge">("off");
  const [knowledgeScopeSelection, setKnowledgeScopeSelection] =
    useState<KnowledgeScopeSelection | null>(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [pendingAttachPreview, setPendingAttachPreview] = useState<PendingAttachPreview | null>(
    null,
  );
  const [attachmentPrepError, setAttachmentPrepError] = useState<Error | null>(null);
  const { resolvedToolCallIds, markResolved, clearResolved } = useResolvedWriteApprovals();
  const { setOpen: setPlanOpen } = useAssistantPlan();
  const { conversationId: routeConversationId } = useParams();
  const navigate = useNavigate();
  const conversationIdRef = useRef<string | undefined>(routeConversationId);
  const createdLocallyRef = useRef<string | null>(null);
  const searchModeRef = useRef<"off" | "web" | "knowledge">("off");
  const knowledgeScopeSelectionRef = useRef<KnowledgeScopeSelection | null>(null);
  /** One-shot scope for a general-chat upload turn (does not flip composer mode). */
  const pendingGeneralDocumentScopeRef = useRef<KnowledgeSearchScopePayload | null>(null);
  const ensureConversationId = useCallback(() => {
    if (conversationIdRef.current) return conversationIdRef.current;
    const id = crypto.randomUUID();
    conversationIdRef.current = id;
    createdLocallyRef.current = id;
    navigate(`/chat/${id}`, { replace: true });
    return id;
  }, [navigate]);

  const onWriteToolCallApprovalStreamPart = useMemo(
    () =>
      createWriteToolCallApprovalStreamHandler({
        isWriteToolName: isSupervisorWriteToolName,
        normalizeProposalInput: normalizeSupervisorProposal,
        setApprovalByToolCallId,
      }),
    []
  );

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  useEffect(() => {
    knowledgeScopeSelectionRef.current = knowledgeScopeSelection;
  }, [knowledgeScopeSelection]);

  const setWebSearchEnabled = useCallback((enabled: boolean) => {
    setSearchMode((prev) => {
      if (enabled) return "web";
      return prev === "web" ? "off" : prev;
    });
  }, []);

  const setKnowledgeSearchEnabled = useCallback((enabled: boolean) => {
    if (enabled) {
      // Keep any existing library selection (toggle-first UX in the + menu).
      setSearchMode("knowledge");
      return;
    }
    setSearchMode((prev) => (prev === "knowledge" ? "off" : prev));
  }, []);

  const onKnowledgeScopeApply = useCallback((selection: KnowledgeScopeSelection) => {
    setKnowledgeScopeSelection(selection);
  }, []);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: apiUrl("/api/supervisor-chat"),
        credentials: "include",
        prepareSendMessagesRequest: async ({ body, headers, messages }) => {
          const token = await getJwtToken();
          const authHeaders: Record<string, string> = token
            ? { Authorization: `Bearer ${token}` }
            : {};
          const mode = searchModeRef.current;
          const generalDocScope = pendingGeneralDocumentScopeRef.current;
          pendingGeneralDocumentScopeRef.current = null;
          const knowledgeSearchScope = generalDocScope
            ? generalDocScope
            : mode === "knowledge"
              ? toKnowledgeSearchScopePayload(knowledgeScopeSelectionRef.current)
              : null;
          const knowledgeSearchEnabled = mode === "knowledge" || Boolean(generalDocScope);
          const reviewTarget = latestReviewCanvasTarget(messages);
          const activeReviewId =
            reviewTarget?.document?.reviewId ?? reviewTarget?.reviewId ?? null;
          return {
            body: {
              ...(body ?? {}),
              conversationId: conversationIdRef.current,
              webSearchEnabled: mode === "web" && !generalDocScope,
              knowledgeSearchEnabled,
              ...(knowledgeSearchScope ? { knowledgeSearchScope } : {}),
              ...(activeReviewId ? { activeReviewId } : {}),
              messages: narrowUiMessagesForMastraThreadChat(messages),
            },
            headers: {
              ...(typeof headers === "object" && headers != null && !("append" in headers)
                ? (headers as Record<string, string>)
                : {}),
              ...authHeaders,
            },
          };
        },
      }),
    []
  );

  const chat = useChat({
    transport,
    onData: (dataPart) => {
      onWriteToolCallApprovalStreamPart(dataPart);
      applyReviewProgressPart(dataPart, setReviewProgress);
      if (dataPart.type === "data-supervisor-route") {
        const data = dataPart.data as { label?: string } | undefined;
        if (data?.label) setRouteLabel(data.label);
      }
      if (dataPart.type === "data-tool-call-approval" || dataPart.type === "data-tool-call-suspended") {
        const data = dataPart.data as {
          runId?: string;
          toolCallId?: string;
          toolName?: string;
          args?: { steps?: QuestionFlowStep[] };
          suspendPayload?: { toolName?: string; args?: { steps?: QuestionFlowStep[] }; steps?: QuestionFlowStep[] };
        };
        const toolName = data.toolName ?? data.suspendPayload?.toolName ?? "";
        if (
          data.runId &&
          data.toolCallId &&
          (toolName === "collect-review-inputs" || toolName === "collectReviewInputs")
        ) {
          const steps =
            data.suspendPayload?.steps ?? data.suspendPayload?.args?.steps ?? data.args?.steps;
          setQuestionRuns((prev) => ({
            ...prev,
            [data.toolCallId!]: {
              runId: data.runId!,
              toolCallId: data.toolCallId!,
              steps,
            },
          }));
        }
      }
    },
  });

  const { messages, setMessages, sendMessage, regenerate, status, error, clearError } = chat;

  const onRegenerate = useCallback(() => {
    void regenerate();
  }, [regenerate]);

  useEffect(() => {
    if (!routeConversationId || createdLocallyRef.current === routeConversationId) return;
    let cancelled = false;
    conversationIdRef.current = routeConversationId;
    void apiFetch<{
      messages?: UIMessage[];
      suspendedReview?: { runId?: string; toolCallId?: string; steps?: QuestionFlowStep[] } | null;
    }>(`/api/supervisor-chat/conversations/${routeConversationId}/messages`)
      .then((data) => {
        if (cancelled) return;
        const nextMessages = data.messages ?? [];
        setMessages(nextMessages);
        setRouteLabel(null);
        setQuestionRuns({
          ...questionRunsFromMessages(nextMessages),
          ...questionRunsFromSuspended(data.suspendedReview),
        });
        setReviewProgress(null);
        setDismissedReviewId(null);
        setLoadedReview(null);
        setApprovalByToolCallId({});
        clearResolved();
        clearError();
      })
      .catch(() => {
        if (!cancelled) {
          setMessages([]);
          setQuestionRuns({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clearError, clearResolved, routeConversationId, setMessages]);

  const announcedConversationRef = useRef<string | null>(null);
  useEffect(() => {
    const id = conversationIdRef.current;
    if (!id || announcedConversationRef.current === id) return;
    if (!messages.some((message) => message.role === "user")) return;
    announcedConversationRef.current = id;
    refreshSupervisorChats();
  }, [messages]);

  const hasUserMessage = messages.some((message) => message.role === "user");
  useEffect(() => {
    if (status !== "ready") return;
    if (!conversationIdRef.current || !hasUserMessage) return;
    refreshSupervisorChats();
    // Title generation is async; poll so Recent shows the LLM title when it lands.
    const timers = [1500, 4000, 8000].map((ms) =>
      window.setTimeout(() => {
        refreshSupervisorChats();
      }, ms),
    );
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [status, hasUserMessage]);

  useEffect(() => {
    const found = questionRunsFromMessages(messages);
    const ids = Object.keys(found);
    if (ids.length === 0) return;
    setQuestionRuns((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of ids) {
        const run = found[id];
        if (!run || next[id]?.runId === run.runId) continue;
        next[id] = { ...next[id], ...run, steps: run.steps ?? next[id]?.steps };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [messages]);
  const appendUserChatMessage = useAppendUserChatMessage(messages, setMessages);

  const activePending = useMemo(
    () =>
      findActiveWriteApproval({
        messages,
        approvalByToolCallId,
        isWriteToolName: isSupervisorWriteToolName,
        normalizeProposalInput: normalizeSupervisorProposal,
        resolvedToolCallIds,
      }),
    [messages, approvalByToolCallId, resolvedToolCallIds]
  );

  const planActivePending = useMemo(
    () =>
      activePending
        ? {
            mode: activePending.mode,
            proposal: activePending.proposal,
            approvalReady: activePending.approvalReady,
          }
        : null,
    [activePending?.mode, activePending?.proposal, activePending?.approvalReady]
  );

  const dockActivePending = useMemo(
    () =>
      activePending
        ? {
            toolCallId: activePending.toolCallId,
            runId: activePending.runId,
            mode: activePending.mode,
            approvalReady: activePending.approvalReady,
          }
        : null,
    [
      activePending?.toolCallId,
      activePending?.runId,
      activePending?.mode,
      activePending?.approvalReady,
    ]
  );

  const onViewPlan = useCallback(() => setPlanOpen(true), [setPlanOpen]);

  const specialist = activePending ? specialistForToolName(activePending.toolName) : null;
  const continuePrompt = useMemo(
    () =>
      specialist?.autoContinuePrompt ??
      `${CONTRACT_TYPE_SPECIALIST.autoContinuePrompt} ${CUSTOM_ROLE_SPECIALIST.autoContinuePrompt}`,
    [specialist?.autoContinuePrompt],
  );

  useAutoContinueTextOnlyWriteProposal({
    status,
    messages,
    actionLoading,
    activePending,
    continuePrompt,
    messageTextContent,
    isWriteToolName: isSupervisorWriteToolName,
    resolvedToolCallIds,
    sendMessage,
  });

  const contractPendingUrl = apiUrl(
    `${CONTRACT_TYPE_SPECIALIST.apiPrefix}/pending?threadScope=${SUPERVISOR_THREAD_SCOPE}`
  );
  const rolePendingUrl = apiUrl(
    `${CUSTOM_ROLE_SPECIALIST.apiPrefix}/pending?threadScope=${SUPERVISOR_THREAD_SCOPE}`
  );

  const { mergePendingApprovalsFromServer: mergeContractPending } = useMergePendingWriteApprovals({
    pendingUrl: contractPendingUrl,
    status,
    messages,
    isWriteToolName: isContractTypeWriteTool,
    normalizeProposalInput: normalizeContractTypeProposal,
    setApprovalByToolCallId,
    resolvedToolCallIds,
    pendingToolCallId:
      specialist?.id === "contract-type" ? activePending?.toolCallId : null,
    approvalReady: specialist?.id === "contract-type" ? activePending?.approvalReady : true,
  });
  const { mergePendingApprovalsFromServer: mergeRolePending } = useMergePendingWriteApprovals({
    pendingUrl: rolePendingUrl,
    status,
    messages,
    isWriteToolName: isCustomRoleWriteTool,
    normalizeProposalInput: normalizeCustomRoleProposal,
    setApprovalByToolCallId,
    resolvedToolCallIds,
    pendingToolCallId: specialist?.id === "custom-role" ? activePending?.toolCallId : null,
    approvalReady: specialist?.id === "custom-role" ? activePending?.approvalReady : true,
  });

  const mergePendingApprovalsFromServer = useCallback(async () => {
    await Promise.all([mergeContractPending(), mergeRolePending()]);
  }, [mergeContractPending, mergeRolePending]);

  const clearApproval = useCallback(
    (toolCallId: string) => {
      markResolved(toolCallId);
      setApprovalByToolCallId((prev) => {
        const copy = { ...prev };
        delete copy[toolCallId];
        return copy;
      });
    },
    [markResolved]
  );

  const postApprovalAction = useCallback(
    async (
      path: "approve" | "decline",
      body: Record<string, unknown>,
      messagesForRequest?: UIMessage[],
      options?: { declineReason?: string }
    ) => {
      const toolName =
        (typeof body.toolCallId === "string"
          ? approvalByToolCallId[body.toolCallId]?.toolName
          : undefined) ??
        activePending?.toolName ??
        "";
      const target = specialistForToolName(toolName) ?? CONTRACT_TYPE_SPECIALIST;
      const requestMessages = dedupeUiMessagesForApiRequest(messagesForRequest ?? messages);
      setActionLoading(true);
      try {
        const token = await getJwtToken();
        const response = await fetch(apiUrl(`${target.apiPrefix}/${path}`), {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            ...body,
            threadScope: SUPERVISOR_THREAD_SCOPE,
            messages: requestMessages,
          }),
        });
        const settledToolCallId = typeof body.toolCallId === "string" ? body.toolCallId : "";
        const parsed = await parseWriteAssistantApproveResponse({
          response,
          path,
          requestMessages,
          settledToolCallId,
          directWrite:
            target.id === "custom-role"
              ? {
                  entityKey: "customRole",
                  isWriteToolName: isCustomRoleWriteTool,
                  buildOutput: (entity) => ({
                    success: true,
                    customRole: {
                      id: String(entity.id ?? ""),
                      name: String(entity.name ?? ""),
                      role_key: String(entity.role_key ?? ""),
                      instructions:
                        typeof entity.instructions === "string" ? entity.instructions : undefined,
                    },
                  }),
                }
              : {
                  entityKey: "contractType",
                  isWriteToolName: isContractTypeWriteTool,
                  buildOutput: (entity) => ({
                    success: true,
                    contractType: {
                      id: String(entity.id ?? ""),
                      name: String(entity.name ?? ""),
                      value: String(entity.value ?? ""),
                      guidelines:
                        typeof entity.guidelines === "string" ? entity.guidelines : null,
                    },
                  }),
                },
        });

        let nextMessages = requestMessages;
        if (parsed.handledAsJson) {
          nextMessages = parsed.nextMessages;
          if (nextMessages !== requestMessages) setMessages(nextMessages);
        } else {
          nextMessages = await mergeUiMessageStream(response, requestMessages, setMessages, {
            onStreamPart: onWriteToolCallApprovalStreamPart,
          });
          const declineReason =
            options?.declineReason ?? (typeof body.reason === "string" ? body.reason : undefined);
          nextMessages = finalizeWriteApprovalStreamMessages({
            messages: nextMessages,
            toolCallId: settledToolCallId,
            isWriteToolName: isSupervisorWriteToolName,
            path,
            declineReason: path === "decline" ? declineReason : undefined,
          });
          setMessages(nextMessages);
          await mergePendingApprovalsFromServer();
        }

        clearError();
        if (path === "approve") {
          const proposal = activePending?.proposal;
          const displayName = typeof proposal?.name === "string" ? proposal.name : "";
          applyWriteApprovalSuccessMessageIfNeeded({
            kind: target.id === "custom-role" ? "custom-role" : "contract-type",
            mode: activePending?.mode ?? "create",
            messages: nextMessages,
            setMessages,
            displayName,
          });
          if (settledToolCallId) clearApproval(settledToolCallId);
        }
      } catch (err: unknown) {
        const raw = err instanceof Error ? err.message : "Request failed";
        console.error("[supervisor-chat/approval]", raw);
      } finally {
        setActionLoading(false);
      }
    },
    [
      activePending,
      approvalByToolCallId,
      clearApproval,
      clearError,
      mergePendingApprovalsFromServer,
      messages,
      onWriteToolCallApprovalStreamPart,
      setMessages,
    ]
  );

  const isStreaming = status === "streaming" || status === "submitted";
  const busy = isStreaming || actionLoading || answering || attachmentUploading;
  const displayError = error ?? attachmentPrepError;

  const approveEdited = useCallback(
    (toolCallId: string, proposal: Record<string, unknown>) => {
      const pending = approvalByToolCallId[toolCallId];
      if (!pending?.runId) return;
      void postApprovalAction("approve", {
        runId: pending.runId,
        toolCallId,
        proposalOverride: proposal,
      });
    },
    [approvalByToolCallId, postApprovalAction]
  );

  const { pendingDockActions, revisePendingProposal } = useAssistantWriteApprovalHandlers({
    activePending: dockActivePending,
    busy,
    appendUserChatMessage,
    postApprovalAction,
    clearApproval,
    onViewPlan,
  });

  useSyncAssistantPlan({
    kind: specialist?.id === "custom-role" ? "custom-role" : "contract-type",
    activePending: planActivePending,
    disabled: busy || !activePending?.approvalReady,
    busy,
    onCreate: () => pendingDockActions?.onCreate(),
  });

  const send = useCallback(
    (text: string, attachment?: SupervisorAttachment) => {
      ensureConversationId();
      setReviewProgress(null);
      setRouteLabel(null);
      setAttachmentPrepError(null);
      if (attachment) {
        // Contract review keeps AI SDK file parts (stripped server-side into contractFile).
        if (isContractReviewAttachmentIntent(text)) {
          void sendMessage({
            text,
            files: [
              {
                type: "file",
                mediaType: attachment.mediaType || "application/octet-stream",
                filename: attachment.name,
                url: attachment.dataUrl,
              },
            ],
          });
          return;
        }

        // General (non-review) attach: upload to per-user library, then persist a durable
        // Attached file marker (with documentId) so chips survive reload and stay viewable.
        void (async () => {
          setAttachmentUploading(true);
          setPendingAttachPreview({ fileName: attachment.name, text });
          try {
            const { documentId } = await uploadGeneralChatAttachment(attachment);
            pendingGeneralDocumentScopeRef.current = { documentIds: [documentId] };
            // Drop provisional bubble before sendMessage adds the real user message (avoids duplicate).
            setPendingAttachPreview(null);
            setAttachmentUploading(false);
            await sendMessage({
              text: buildUserTextWithAttachmentMarker(text, attachment.name, documentId),
            });
          } catch (err) {
            pendingGeneralDocumentScopeRef.current = null;
            setAttachmentUploading(false);
            setPendingAttachPreview(null);
            setAttachmentPrepError(
              err instanceof Error ? err : new Error("Could not upload the document."),
            );
          }
        })();
        return;
      }
      void submitAssistantChatMessage({
        text,
        status,
        actionLoading,
        activePending,
        appendUserChatMessage,
        revisePendingProposal,
        sendMessage,
      });
    },
    [
      actionLoading,
      activePending,
      appendUserChatMessage,
      revisePendingProposal,
      ensureConversationId,
      sendMessage,
      status,
    ]
  );

  const submitAnswers = useCallback(
    async (
      toolCallId: string,
      answers: Record<string, string[]>,
      fallbackRunId?: string,
      extras?: { supportingDocuments?: Array<{ fileName: string; docType: string; fileBase64: string }> }
    ) => {
      const stored = questionRuns[toolCallId];
      const runId = stored?.runId || fallbackRunId;
      if (!runId) throw new Error("This question is no longer waiting for an answer.");
      setAnswering(true);
      setReviewProgress(null);
      try {
        const token = await getJwtToken();
        const response = await fetch(apiUrl("/api/supervisor-chat/answer"), {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            runId,
            toolCallId,
            answers,
            ...(extras?.supportingDocuments?.length
              ? { supportingDocuments: extras.supportingDocuments }
              : {}),
            conversationId: conversationIdRef.current,
            messages,
          }),
        });
        if (!response.ok || !response.body) {
          throw new Error(answerErrorMessage(await response.text()));
        }
        await mergeUiMessageStream(response, messages, setMessages, {
          onStreamPart: (part) => {
            onWriteToolCallApprovalStreamPart(part);
            applyReviewProgressPart(part, setReviewProgress);
          },
        });
        setQuestionRuns((prev) => {
          const copy = { ...prev };
          delete copy[toolCallId];
          return copy;
        });
        clearError();
        setReviewProgress((prev) => (prev ? { ...prev, isConnected: false } : prev));
      } finally {
        setAnswering(false);
      }
    },
    [clearError, messages, onWriteToolCallApprovalStreamPart, questionRuns, setMessages]
  );

  const reviewTarget = useMemo(() => latestReviewCanvasTarget(messages), [messages]);

  useEffect(() => {
    const reviewId = reviewTarget?.document?.reviewId ?? reviewTarget?.reviewId;
    if (!reviewId || reviewId === dismissedReviewId) {
      setLoadedReview(null);
      return;
    }
    let cancelled = false;
    void apiFetch<ReviewApiRow>(`/api/reviews/${reviewId}`)
      .then((review) => {
        if (cancelled) return;
        const fromTool = reviewTarget?.document;
        if (fromTool && fromTool.reviewId === reviewId) {
          setLoadedReview(mergeReviewConfig(fromTool, reviewConfigFromApi(review)));
          return;
        }
        setLoadedReview(canvasDocumentFromReview(review));
      })
      .catch(() => {
        if (!cancelled) setLoadedReview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dismissedReviewId, reviewTarget]);

  const reviewDocument = loadedReview ?? reviewTarget?.document;
  const canvasDocument =
    reviewDocument && reviewDocument.reviewId !== dismissedReviewId ? reviewDocument : null;

  useEffect(() => {
    if (!canvasDocument) return;
    setReviewProgress((prev) => (prev?.isConnected ? { ...prev, isConnected: false } : prev));
  }, [canvasDocument]);

  useEffect(() => {
    if (status !== "ready") return;
    setReviewProgress((prev) => (prev?.isConnected ? { ...prev, isConnected: false } : prev));
  }, [status]);
  const questionValue = useMemo(
    () => ({
      runs: questionRuns,
      answering,
      submitAnswers,
      reviewProgress,
    }),
    [answering, questionRuns, reviewProgress, submitAnswers]
  );

  const clearCanvasCloseTimer = useCallback(() => {
    if (canvasCloseTimerRef.current != null) {
      clearTimeout(canvasCloseTimerRef.current);
      canvasCloseTimerRef.current = null;
    }
  }, []);

  const startNewConversation = useCallback(() => {
    createdLocallyRef.current = null;
    conversationIdRef.current = undefined;
    setMessages([]);
    setApprovalByToolCallId({});
    clearResolved();
    setRouteLabel(null);
    setQuestionRuns({});
    clearCanvasCloseTimer();
    setCanvasClosing(false);
    setDismissedReviewId(null);
    setLoadedReview(null);
    setReviewProgress(null);
    setCanvasSize("docked");
    setSearchMode("off");
    setKnowledgeScopeSelection(null);
    setAttachmentPrepError(null);
    setAttachmentUploading(false);
    setPendingAttachPreview(null);
    pendingGeneralDocumentScopeRef.current = null;
    clearError();
    navigate("/chat");
  }, [clearCanvasCloseTimer, clearError, clearResolved, navigate, setMessages]);

  useEffect(() => {
    function onNewChat() {
      startNewConversation();
    }
    window.addEventListener(SUPERVISOR_NEW_CHAT, onNewChat);
    return () => window.removeEventListener(SUPERVISOR_NEW_CHAT, onNewChat);
  }, [startNewConversation]);

  function toggleCanvasExpanded() {
    if (canvasClosing) return;
    setCanvasSize((prev) => nextCanvasSize(prev));
  }

  function closeCanvas(reviewId: string) {
    setDismissedReviewId(reviewId);
    setCanvasSize("docked");
  }

  function beginCloseCanvas(reviewId: string) {
    if (canvasClosing) return;
    setCanvasClosing(true);
    setCanvasSize("docked");
    clearCanvasCloseTimer();
    canvasCloseTimerRef.current = setTimeout(() => {
      closeCanvas(reviewId);
      setCanvasClosing(false);
      canvasCloseTimerRef.current = null;
    }, CANVAS_CLOSE_MS);
  }

  function openCanvas() {
    clearCanvasCloseTimer();
    setCanvasClosing(false);
    setDismissedReviewId(null);
    setCanvasSize("docked");
  }

  useEffect(() => () => clearCanvasCloseTimer(), [clearCanvasCloseTimer]);

  const approvalValue = useMemo(
    () => ({
      busy,
      activePending,
      pendingDockActions,
      approveEdited,
      routeLabel,
    }),
    [activePending, approveEdited, busy, pendingDockActions, routeLabel]
  );

  return (
    <SupervisorApprovalProvider value={approvalValue}>
      <ReviewQuestionProvider value={questionValue}>
        <AssistantPanelPlanSplit
          className={cn(
            "h-full rounded-none border-0 bg-[var(--chat-shell-bg)] shadow-none dark:border-0 dark:shadow-none",
            darkPageCanvasWashClassName,
          )}
          header={null}
          messageArea={
            <>
              <CanvasSplit>
                <CanvasSplitThread
                  rail={Boolean(canvasDocument) && canvasSize === "docked"}
                  collapsed={Boolean(canvasDocument) && canvasSize === "expanded" && !canvasClosing}
                >
                  <div className="flex min-h-0 flex-1 flex-col">
                    {displayError ? (
                      <div className="px-4 pt-3">
                        <AssistantChatErrorNotice
                          error={displayError}
                          onStartNewConversation={startNewConversation}
                          startNewConversationLoading={false}
                        />
                      </div>
                    ) : null}
                    <SupervisorThread
                      messages={messages}
                      busy={busy}
                      streaming={isStreaming}
                      isEmpty={messages.length === 0}
                      onSend={send}
                      onRegenerate={onRegenerate}
                      closedReviewId={
                        canvasDocument || dismissedReviewId == null
                          ? null
                          : reviewTarget?.document?.reviewId === dismissedReviewId ||
                              reviewTarget?.reviewId === dismissedReviewId
                            ? dismissedReviewId
                            : null
                      }
                      onOpenCanvas={openCanvas}
                      compactComposer={Boolean(canvasDocument) && canvasSize === "docked"}
                      threadId={routeConversationId}
                      draftKey={routeConversationId}
                      webSearchEnabled={searchMode === "web"}
                      knowledgeSearchEnabled={searchMode === "knowledge"}
                      knowledgeScopeSelection={knowledgeScopeSelection}
                      onWebSearchChange={setWebSearchEnabled}
                      onKnowledgeSearchChange={setKnowledgeSearchEnabled}
                      onKnowledgeScopeApply={onKnowledgeScopeApply}
                      attachmentUploading={attachmentUploading}
                      pendingAttachPreview={pendingAttachPreview}
                      hasFinishedReview={Boolean(
                        reviewTarget?.document?.reviewId ?? reviewTarget?.reviewId
                      )}
                    />
                  </div>
                </CanvasSplitThread>
                {canvasDocument ? (
                  <CanvasSplitDocument
                    presence={canvasClosing ? "closing" : "open"}
                    expanded={canvasSize === "expanded" && !canvasClosing}
                  >
                    <CanvasErrorBoundary key={canvasDocument.reviewId}>
                      <ReviewResultsCanvas
                        document={canvasDocument}
                        writing={false}
                        size={canvasSize}
                        onSize={toggleCanvasExpanded}
                        onClose={() => beginCloseCanvas(canvasDocument.reviewId)}
                      />
                    </CanvasErrorBoundary>
                  </CanvasSplitDocument>
                ) : null}
              </CanvasSplit>
            </>
          }
          composer={null}
        />
      </ReviewQuestionProvider>
    </SupervisorApprovalProvider>
  );
}

class CanvasErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error("Review canvas failed to render", error);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex h-full min-h-0 flex-1 items-center justify-center px-6 text-center">
          <p className="text-sm text-[#576274] dark:text-slate-300">
            The findings table couldn’t be displayed.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

class SupervisorChatErrorBoundary extends Component<{ children: ReactNode }, { message: string | null }> {
  state = { message: null as string | null };

  static getDerivedStateFromError(error: Error) {
    return { message: error instanceof Error ? error.message : "Chat couldn't display this reply" };
  }

  componentDidCatch(error: Error) {
    console.error("Supervisor chat failed to render", error);
  }

  render() {
    if (this.state.message) {
      return (
        <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="max-w-md text-sm text-[#101828] dark:text-white">{this.state.message}</p>
          <button
            type="button"
            className="rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function SupervisorChatPage() {
  return (
    <div className="flex h-[calc(100dvh-3rem)] min-h-0 flex-col">
      <AssistantPlanProvider>
        <SupervisorChatErrorBoundary>
          <SupervisorChatSession />
        </SupervisorChatErrorBoundary>
      </AssistantPlanProvider>
    </div>
  );
}
